import React, { createContext, useContext, useState, useCallback } from 'react';

type TourSegment = 'home' | 'strategist' | null;

interface TourContextType {
  hasSeenTour: boolean;
  shouldStartTour: boolean;
  currentSegment: TourSegment;
  startTour: () => void;
  completeTour: () => void;
  dismissTour: () => void;
  advanceToSegment: (segment: TourSegment) => void;
}

const TourContext = createContext<TourContextType>({
  hasSeenTour: false,
  shouldStartTour: false,
  currentSegment: null,
  startTour: () => {},
  completeTour: () => {},
  dismissTour: () => {},
  advanceToSegment: () => {},
});

export const useTour = () => useContext(TourContext);

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasSeenTour, setHasSeenTour] = useState(() => localStorage.getItem('tour_completed') === 'true');
  const [shouldStartTour, setShouldStartTour] = useState(false);
  const [currentSegment, setCurrentSegment] = useState<TourSegment>(null);

  const startTour = useCallback(() => {
    setShouldStartTour(true);
    setCurrentSegment('home');
  }, []);

  const completeTour = useCallback(() => {
    localStorage.setItem('tour_completed', 'true');
    setHasSeenTour(true);
    setShouldStartTour(false);
    setCurrentSegment(null);
  }, []);

  const dismissTour = useCallback(() => {
    localStorage.setItem('tour_completed', 'true');
    setHasSeenTour(true);
    setShouldStartTour(false);
    setCurrentSegment(null);
  }, []);

  const advanceToSegment = useCallback((segment: TourSegment) => {
    setCurrentSegment(segment);
  }, []);

  return (
    <TourContext.Provider
      value={{
        hasSeenTour,
        shouldStartTour,
        currentSegment,
        startTour,
        completeTour,
        dismissTour,
        advanceToSegment,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};
